import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DatePicker } from '@/components/ui/date-picker';
import { invoicesApi } from '@/apis/invoices';
import {
  INVOICE_STATUS_OPTIONS,
  InvoiceSelect,
  downloadInvoicesCsv,
} from './invoice-shared';

const schema = z
  .object({
    status: z.string().min(1, 'Select a status'),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  })
  .refine((v) => !v.startDate || !v.endDate || v.startDate <= v.endDate, {
    message: 'End date must be after the start date',
    path: ['endDate'],
  });
type Values = z.infer<typeof schema>;

const labelClass = 'mb-1.5 block text-sm font-medium text-black';
const selectClass = 'h-10 rounded-xl px-3 py-0 text-base';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  ...INVOICE_STATUS_OPTIONS,
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InvoiceExportDialog({ open, onOpenChange }: Props) {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { status: 'all', startDate: '', endDate: '' },
  });

  useEffect(() => {
    if (open) reset({ status: 'all', startDate: '', endDate: '' });
  }, [open, reset]);

  const onExport = async (v: Values) => {
    try {
      const res = await invoicesApi.getInvoices({
        page: 1,
        limit: 1000,
        status: v.status === 'all' ? undefined : v.status,
        startDate: v.startDate || undefined,
        endDate: v.endDate || undefined,
      });
      if (!res.data.length) {
        toast.info('No invoices match the selected filters');
        return;
      }
      downloadInvoicesCsv(res.data, `invoices-${new Date().toISOString().slice(0, 10)}.csv`);
      onOpenChange(false);
    } catch {
      toast.error('Export failed. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!isSubmitting) onOpenChange(o);
      }}
    >
      <DialogContent
        className="w-[560px] max-w-[92vw]"
        closeButtonClassName="text-danger hover:bg-danger-light"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl">Export Invoices</DialogTitle>
        </DialogHeader>
        <p className="border-b border-smoke pb-4 text-sm text-lightGray">
          You can export your invoice data in CSV format.
        </p>
        <form onSubmit={handleSubmit(onExport)} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>
                Module <span className="text-danger">*</span>
              </label>
              <InvoiceSelect
                value="invoices"
                onChange={() => {}}
                options={[{ value: 'invoices', label: 'Invoices' }]}
                disabled
                className={selectClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                Status <span className="text-danger">*</span>
              </label>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <InvoiceSelect
                    value={field.value}
                    onChange={field.onChange}
                    options={STATUS_OPTIONS}
                    placeholder="Select Status"
                    className={selectClass}
                  />
                )}
              />
              {errors.status && (
                <p className="mt-1 text-xs text-danger">{errors.status.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>Date range</label>
            <div className="grid grid-cols-2 gap-4">
              <Controller
                control={control}
                name="startDate"
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    // Mirrors the schema rule: end date >= start date
                    max={watch('endDate') || undefined}
                  />
                )}
              />
              <Controller
                control={control}
                name="endDate"
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    // Mirrors the schema rule: end date >= start date
                    min={watch('startDate') || undefined}
                  />
                )}
              />
            </div>
            {errors.endDate && (
              <p className="mt-1 text-xs text-danger">{errors.endDate.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="rounded-full bg-primary-s50 px-8 py-2.5 text-sm font-medium text-primary-s500 hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-full bg-primary-s500 px-8 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
